
import React, { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, differenceInDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/layout';
import { mealPlanService } from '@/services/mealPlan';
import { recipeService } from '@/services/recipe';
import { mealCompositionService } from '@/services/mealComposition';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { RECIPES, MEAL_PLANS, MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import CalendarDay from '@/components/calendar/CalendarDay';
import CalendarStats from '@/components/calendar/CalendarStats';
import WeeklyWellnessScores from '@/components/calendar/WeeklyWellnessScores';
import { Skeleton } from '@/components/ui/skeleton';
import ErrorAlert from '@/components/meal-planner/ErrorAlert';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { getLastCompleteWeek, formatWeekRangeSubtitle, formatWeekRangeWithDays, getLast30Days } from '@/utils/timeUtils';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ExternalLink } from 'lucide-react';

export default function CalendarHistory() {
  const [wellnessWeekDates, setWellnessWeekDates] = useState<{ start: Date; end: Date } | null>(null);
  
  // Fallback to last complete week if no wellness data
  const { start: fallbackWeekStart, end: fallbackWeekEnd } = getLastCompleteWeek();
  const { start: thirtyDaysStart, end: thirtyDaysEnd } = getLast30Days();
  
  // Use wellness dates if available, otherwise fallback to calculated dates
  const weekStart = wellnessWeekDates?.start || fallbackWeekStart;
  const weekEnd = wellnessWeekDates?.end || fallbackWeekEnd;
  
  const weekRange = formatWeekRangeSubtitle(weekStart, weekEnd);
  const detailedWeekRange = formatWeekRangeWithDays(weekStart, weekEnd);
  
  const { familyId, isLoading: familyLoading } = useCurrentUserFamily();
  const { track } = useAnalytics();

  // Generate array of days based on actual date range from database
  const numberOfDays = differenceInDays(weekEnd, weekStart) + 1; // +1 to include end date
  const weekDays = Array.from({ length: numberOfDays }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });

  const handleWellnessDatesAvailable = useCallback((startDate: Date, endDate: Date) => {
    setWellnessWeekDates({ start: startDate, end: endDate });
  }, []);


  // Fetch all recipes
  const { data: recipes = [], isLoading: recipesLoading, refetch: refetchRecipes } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
    enabled: !!familyId,
  });

  // Fetch all compositions
  const { data: compositions = [], isLoading: compositionsLoading, refetch: refetchCompositions } = useQuery({
    queryKey: [MEAL_COMPOSITIONS],
    queryFn: mealCompositionService.getMealCompositions,
    enabled: !!familyId,
  });

  // Fetch meal plans for the last complete week
  const { data: weekMealPlans = [], isLoading: mealPlansLoading, error, refetch: refetchMealPlans } = useQuery({
    queryKey: [MEAL_PLANS, format(weekStart, 'yyyy-MM-dd'), format(weekEnd, 'yyyy-MM-dd')],
    queryFn: () => mealPlanService.getMealPlans(weekStart, weekEnd),
    enabled: !!familyId,
  });

  // Fetch meal plans for the last 30 days for stats
  const { data: thirtyDayMealPlans = [], isLoading: thirtyDayPlansLoading, refetch: refetchThirtyDayPlans } = useQuery({
    queryKey: [MEAL_PLANS, format(thirtyDaysStart, 'yyyy-MM-dd'), format(thirtyDaysEnd, 'yyyy-MM-dd'), '30-day'],
    queryFn: () => mealPlanService.getMealPlans(thirtyDaysStart, thirtyDaysEnd),
    enabled: !!familyId,
  });


  const isLoading = recipesLoading || mealPlansLoading || familyLoading || compositionsLoading;

  // Use meal plans directly (no need to flatten)
  const allMealPlans = weekMealPlans;
  
  // Use 30-day meal plans directly (no need to flatten)
  const allThirtyDayMealPlans = thirtyDayMealPlans;

  // Track when wellness calendar is visited
  useEffect(() => {
    track('wellness_calendar_visited', {
      week_range: weekRange,
      has_meal_plans: weekMealPlans.length > 0,
      total_days_with_plans: new Set(weekMealPlans.map(mp => mp.date)).size,
    });
  }, [track, weekRange, weekMealPlans]);

  // Track when week data is loaded (success/failure)
  useEffect(() => {
    if (!mealPlansLoading) {
      if (error) {
        track('wellness_week_data_loaded', {
          success: false,
          error_message: (error as Error).message,
          week_range: weekRange,
        });
      } else {
        track('wellness_week_data_loaded', {
          success: true,
          meal_plans_count: allMealPlans.length,
          days_with_data: new Set(weekMealPlans.map(mp => mp.date)).size,
          week_range: weekRange,
        });
      }
    }
  }, [mealPlansLoading, error, allMealPlans.length, weekMealPlans, track, weekRange]);

  // Helper function to get recipes and compositions for a date
  const getItemsForDate = (date: Date): { type: 'recipe' | 'composition'; item: Recipe | MealCompositionWithRecipes; mealType: 'breakfast' | 'lunch' | 'dinner'; authorFirstName?: string }[] => {
    const dateString = format(date, 'yyyy-MM-dd');
    const dateMealPlans = weekMealPlans.filter(mp => mp.date === dateString);
    
    if (dateMealPlans.length === 0) return [];
    
    const items: { type: 'recipe' | 'composition'; item: Recipe | MealCompositionWithRecipes; mealType: 'breakfast' | 'lunch' | 'dinner'; authorFirstName?: string }[] = [];
    
    for (const mealPlan of dateMealPlans) {
      if (mealPlan.recipeId) {
        const recipe = recipes.find(recipe => recipe.id === mealPlan.recipeId);
        if (recipe) {
          items.push({ type: 'recipe', item: recipe, mealType: mealPlan.mealType, authorFirstName: mealPlan.authorFirstName });
        }
      }
      
      if (mealPlan.mealCompositionId) {
        const composition = compositions.find(comp => comp.id === mealPlan.mealCompositionId);
        if (composition) {
          items.push({ type: 'composition', item: composition, mealType: mealPlan.mealType, authorFirstName: mealPlan.authorFirstName });
        }
      }
    }
    
    return items;
  };

  return (
    <PageLayout
      title="Weekly Wellness Meal Review"
      description={`Insights for week of ${detailedWeekRange}`}
    >
      <div className="pb-14">
        {error && (
          <ErrorAlert 
            title="Error Loading Weekly Meal Plans" 
            description={(error as Error).message}
          />
        )}

        {/* Weekly Wellness Assessment */}
        <WeeklyWellnessScores onDatesAvailable={handleWellnessDatesAvailable} />

        {/* Calendar Days Grid */}
        <div className="space-y-3 mb-8">
          <h3 className="text-lg font-semibold mb-4">What you ate last week</h3>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: numberOfDays }).map((_, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-4 w-48" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {weekDays.map((date) => {
                const itemsData = getItemsForDate(date);
                const today = new Date();
                return (
                  <CalendarDay
                    key={date.toISOString()}
                    date={date}
                    itemsData={itemsData.length > 0 ? itemsData : null}
                    isToday={format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* 30-Day Planning Review */}
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">30-Day Planning Review</h3>
            <Link
              to="/monthly-meal-view"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              See full month
            </Link>
          </div>
          <CalendarStats 
            mealPlans={allThirtyDayMealPlans}
            recipes={recipes}
            compositions={compositions}
            isLoading={thirtyDayPlansLoading || recipesLoading || compositionsLoading}
            totalDays={30}
          />
        </div>
      </div>
    </PageLayout>
  );
}
